import { useState } from "react";
import { useLegalProvisionDocumentQuery } from "./hooks/useLegalProvisionDocumentQuery";
import { useLegalProvisionDocumentCommand } from "./hooks/useLegalProvisionDocumentCommand";

import Table from "../../common/components/Table";
import IconButton from "../../common/components/IconButton";
import Pagination from "../../common/components/Pagination";
import Button from "../../common/components/Button";
import { DateUtil } from "../../common/utils/date.util";
import { ROUTES } from "../../core/routes/routes";
import { useNavigate } from "react-router-dom";
import { BsClipboardPlus, BsPencilSquare, BsTrash, BsXCircle } from "react-icons/bs";

function Indicators() {
  const navigate = useNavigate();
  const { documents, loading, totalPages, page, onChangePage, refresh } =
    useLegalProvisionDocumentQuery();
  const {
    deleteLegalProvisionDocument,
    updateLegalProvisionDocument,
    editData,
    setEditDefaultValues,
    markDocumentForDeletion,
    addNewFile,
    errors,
    loading: commandLoading,
  } = useLegalProvisionDocumentCommand(refresh);

  const [showEditModal, setShowEditModal] = useState(false);

  const handleEdit = (item) => {
    setEditDefaultValues(item);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    await updateLegalProvisionDocument();
    setShowEditModal(false);
  };

  const handleDelete = (item) => {
    const documentIds = item.documents.map((doc) => doc.id);
    deleteLegalProvisionDocument(documentIds);
  };

  const columns = [
    {
      header: "Fecha siniestro",
      accessor: "dateAccident",
      render: (value) => DateUtil.format(value),
    },
    { header: "Nombre implicado", accessor: "namePersonInvolved" },
    { header: "Documento del implicado", accessor: "documentPersonInvolved" },
    { header: "Placa del vehículo", accessor: "vehiclePlate" },
    { header: "Lugar del siniestro", accessor: "placeOfEvent" },
    {
      header: "Documentos",
      accessor: "documents",
      render: (value) => (
        <div className="d-flex flex-wrap gap-2">
          {value.map((doc, index) => (
            <a key={doc.id} href={doc.url} target="_blank" rel="noreferrer">
              Documento {index + 1}
            </a>
          ))}
        </div>
      ),
    },
    {
      header: "Acciones",
      accessor: "actions",
      render: (_, item) => (
        <div className="d-flex align-items-center gap-2">
          <IconButton tooltipText="Editar" onClick={() => handleEdit(item)}>
            <BsPencilSquare size={20} />
          </IconButton>
          <IconButton tooltipText="Eliminar" onClick={() => handleDelete(item)}>
            <BsTrash size={20} color="#dc3545" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <main className="container-fluid p-4">
      <header className="d-flex justify-content-between align-items-center mb-4 px-3 py-2 border-bottom">
        <h1 className="h2 m-0 fw-bold">Documentos de provisiones legales</h1>
        <div className="d-flex gap-2">
          <IconButton
            onClick={() => navigate(ROUTES.REGISTER_LEGAL_PROVISION_DOCUMENTS)}
          >
            <BsClipboardPlus size={24} />
          </IconButton>
        </div>
      </header>

      <section className="shadow w-100 d-flex flex-column">
        <Table columns={columns} data={documents} loading={loading} showIndex />

        <Pagination
          totalPages={totalPages}
          page={page}
          onPageChange={onChangePage}
        />
      </section>

      {showEditModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="bg-white rounded shadow p-4"
            style={{
              maxWidth: 700,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-3">Editar documentos de provisión legal</h3>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Documentos existentes
              </label>
              {editData.existingDocuments.length === 0 ? (
                <p className="text-muted">No quedan documentos existentes.</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {editData.existingDocuments.map((doc, index) => (
                    <div
                      key={doc.id}
                      className="d-flex align-items-center gap-2 border rounded p-2"
                    >
                      <a href={doc.url} target="_blank" rel="noreferrer" className="flex-grow-1">
                        Documento {index + 1}
                      </a>
                      <IconButton
                        tooltipText="Quitar documento"
                        onClick={() => markDocumentForDeletion(doc.id)}
                      >
                        <BsXCircle size={18} color="#dc3545" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Agregar nuevos documentos
              </label>
              <div className="row row-cols-1 row-cols-md-2 g-3">
                {Array.from({ length: editData.newFiles.length + 1 }).map(
                  (_, index) => (
                    <div className="mb-2" key={index}>
                      <input
                        type="file"
                        className={`form-control ${
                          errors.errors?.files ? "is-invalid" : ""
                        }`}
                        onChange={(e) => addNewFile(e.target.files[0] || null)}
                      />
                    </div>
                  )
                )}
              </div>
              {errors.errors?.files && (
                <div className="text-danger small mt-1">
                  {errors.errors.files}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancelar
              </button>
              <Button
                type="button"
                loading={commandLoading}
                onClick={handleUpdate}
              >
                Guardar cambios
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Indicators;
